import React, { useState } from 'react';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions, 
  Button, Typography, Box, Divider, 
  TextField, InputAdornment, IconButton, Chip,
  MenuItem, Select, FormControl, InputLabel,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { DataGrid } from '@mui/x-data-grid';
import { format } from 'date-fns';
import analyticsApi from '../../../services/analyticsapi';
import apiClient from '../../../services/api';

/**
 * Modal component to display out of stock items
 */
const OutOfStockList = ({ open, onClose, data, fullScreen = false }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [timePeriod, setTimePeriod] = useState(data?.time_period_days || 30);
  const [localData, setLocalData] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Update local state when data prop changes
  React.useEffect(() => {
    if (data) {
      setLocalData(null); // Reset local data
      setTimePeriod(data.time_period_days || 30);
    }
  }, [data]);
  
  if (!data) {
    return null;
  }
  
  // Use localData if available, otherwise use the prop data
  const displayData = localData || data;

  const handleDownloadCSV = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/analytics/out-of-stock/csv-download/?days=${timePeriod}`, {
        responseType: 'blob'
      });
      
      const blob = response.data;
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `out_of_stock_report_${timePeriod}days.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Error downloading CSV:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = displayData.items?.filter(item => 
    item.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.variant_sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.category && item.category.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (item.vendor_name && item.vendor_name.toLowerCase().includes(searchTerm.toLowerCase()))
  ) || [];

  const columns = [
    { field: 'variant_sku', headerName: 'SKU', width: 70 },
    { field: 'product_name', headerName: 'Product', width: 120,flex: 1 },
    { field: 'vendor_name', headerName: 'Vendor', width: 150 },
    { field: 'category', headerName: 'Category', width: 130 },
    { 
      field: 'days_out_of_stock', 
      headerName: 'Days Out of Stock', 
      width: 100,
      renderCell: (params) => (
        <Chip 
          label={`${params.value} days`} 
          color={params.value > 30 ? "error" : "warning"} 
          size="small" 
        />
      )
    },
    { field: 'total_sales', headerName: 'Sales Units', width: 60, type: 'number' },
    { 
      field: 'potential_sales_value', 
      headerName: 'Potential Value', 
      width: 150,
      valueFormatter: (params) => {
        if (params === null || params === undefined) {
          return '₹ 0';
        }
        return `₹ ${params.toLocaleString('en-IN')}`;
      }
    },
    { 
      field: 'out_of_stock_date', 
      headerName: 'Out of Stock Since', 
      width: 150,
      valueFormatter: (params) => {
        if (!params) {
          return 'Unknown';
        }
        try {
          return format(new Date(params), 'dd/MM/yyyy');
        } catch (error) {
          return 'Invalid date';
        }
      }
    },
    { 
      field: 'last_sale_date', 
      headerName: 'Last Sale', 
      width: 150,
      valueFormatter: (params) => {
        if (!params) {
          return 'Never';
        }
        try {
          return format(new Date(params), 'dd/MM/yyyy');
        } catch (error) {
          return 'Invalid date';
        }
      }
    }
  ];

  // Create a vendor summary table
  const vendorSummary = displayData.vendor_summary || [];
  
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={fullScreen}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
          Out of Stock Analysis
        </Typography>
        <Button 
          onClick={onClose} 
          color="inherit" 
          sx={{ minWidth: 'auto', p: 1 }}
        >
          <CloseIcon />
        </Button>
      </DialogTitle>
      
      <DialogContent dividers>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Summary</Typography>
            <Typography variant="body1">
              <strong>Total Items:</strong> {displayData.total_count || 0} SKUs
            </Typography>
            <Typography variant="body1">
              <strong>Total Units Sold:</strong> {displayData.total_sales?.toLocaleString('en-IN') || 0}
            </Typography>
            <Typography variant="body1">
              <strong>Potential Value:</strong> ₹ {displayData.total_potential_value?.toLocaleString('en-IN') || 0}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ minWidth: 120 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Time Period</InputLabel>
                <Select
                  value={timePeriod}
                  label="Time Period"
                  disabled={loading}
                  onChange={async (e) => {
                    const newPeriod = e.target.value;
                    setTimePeriod(newPeriod);
                    
                    try {
                      setLoading(true);
                      const response = await analyticsApi.viewOutOfStockList(newPeriod);
                      setLocalData(response.data);
                    } catch (error) {
                      console.error("Failed to fetch out of stock data:", error);
                    } finally {
                      setLoading(false);
                    }
                  }}
                >
                  <MenuItem value={30}>Last 30 Days</MenuItem>
                  <MenuItem value={60}>Last 60 Days</MenuItem>
                  <MenuItem value={90}>Last 90 Days</MenuItem>
                  <MenuItem value={180}>Last 180 Days</MenuItem>
                  <MenuItem value={365}>Last 365 Days</MenuItem>
                </Select>
              </FormControl>
              {loading && <CircularProgress size={24} sx={{ ml: 2 }} />}
            </Box>
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton edge="end" size="small">
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ width: '250px' }}
            />
          </Box>
        </Box>
        
        {/* {vendorSummary.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Vendor Summary</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {vendorSummary.map((vendor, index) => (
                <Chip
                  key={index}
                  label={`${vendor.vendor_name}: ${vendor.count} items (₹${vendor.potential_value?.toLocaleString('en-IN')})`}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )} */}
        
        <Divider sx={{ my: 2 }} />
        
        <Box sx={{ height: 'calc(100vh - 300px)', width: '100%' }}>
          <DataGrid
            rows={filteredItems}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            getRowId={(row) => row.variant_id}
            density="standard"
            loading={loading}
            sx={{
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
            }}
          />
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button 
          variant="contained" 
          color="primary"
          onClick={handleDownloadCSV}
          disabled={loading}
        >
          Download CSV Report
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OutOfStockList;